import { Component, signal, OnInit, AfterViewInit, computed, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CoursesService, CursoProgreso } from '../../services/courses.service';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { EventosService, EventoEnVivo } from '../../services/eventos.service';

interface Course {
  id: number;
  title: string;
  description: string;
  image: string;
  imageColor: string;
  tags: string[];
  rating: number;
  startDate: string;
  porcentaje: number;
  clases_completadas: number;
  total_clases: number;
  es_premium: boolean;
}

interface ScheduleItem {
  id: number;
  date: string;
  title: string;
  time: string;
  progress: string;
}

interface CalendarDay {
  day: string;
  date: number;
  fullDate: Date;
  active?: boolean;
  hasEvent?: boolean;
  eventos?: EventoEnVivo[];
}

interface LiveEvent {
  id: number;
  title: string;
  instructor: string;
  participants: number;
  duration: string;
  category: string;
  thumbnail: string;
  color: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './home.html',
  styleUrl: './home.css',
})
export class HomeComponent implements OnInit, AfterViewInit {
  // Inyección de servicios usando inject()
  public router = inject(Router);
  private coursesService = inject(CoursesService);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private eventosService = inject(EventosService);

  activeFilter = signal<string>('All');
  currentMonth = signal<string>('');
  isLoading = signal(false);
  eventosReales = signal<LiveEvent[]>([]);
  eventosCalendario = signal<EventoEnVivo[]>([]);
  calendarDaysSignal = signal<CalendarDay[]>([]);
  @ViewChild('liveCarousel') liveCarouselRef?: ElementRef<HTMLDivElement>;
  canScrollPrev = signal(false);
  canScrollNext = signal(false);
  
  filters = ['All', 'Active', 'Upcoming', 'Completed'];

  // Datos reales del backend
  cursosReales = signal<Course[]>([]);
  currentUser = this.authService.currentUser;

  // Computed para filtrar cursos
  courses = computed(() => {
    const filter = this.activeFilter();
    const cursos = this.cursosReales();
    
    switch(filter) {
      case 'Active':
        return cursos.filter(c => c.porcentaje > 0 && c.porcentaje < 100);
      case 'Completed':
        return cursos.filter(c => c.porcentaje >= 100);
      case 'Upcoming':
        return cursos.filter(c => c.porcentaje === 0);
      default:
        return cursos;
    }
  });

  ngOnInit() {
    window.scrollTo(0, 0);
    
    // Solo cargar datos si hay sesión activa
    if (this.authService.isLoggedIn()) {
      this.setCurrentMonth();
      this.generateCalendarDays();
      this.loadProgreso();
      this.loadEventosEnVivo();
      
      // Cargar eventos del calendario después de generar los días
      setTimeout(() => {
        this.loadEventosCalendario();
      }, 100);
    }
  }

  ngAfterViewInit() {
    // Esperar a que el DOM se renderice completamente
    setTimeout(() => {
      this.updateCarouselControls();
    }, 200);
  }

  /**
   * Carga el progreso de cursos del estudiante
   */
  loadProgreso() {
    this.isLoading.set(true);
    
    this.coursesService.getEstadisticasProgreso().subscribe({
      next: (response) => {
        this.isLoading.set(false);
        
        if (response.ok && response.data) {
          // Mapear datos del backend a la estructura del componente
          const cursosMapeados = response.data.map(this.mapCursoToCard.bind(this));
          this.cursosReales.set(cursosMapeados);
          
          // Solo notificar si hay error, no en carga exitosa
          console.log(`✅ ${response.data.length} cursos cargados exitosamente`);
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudieron cargar los cursos'
          );
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al cargar progreso:', error);
        this.notificationService.error(
          'Error de Conexión',
          'No se pudo conectar con el servidor'
        );
      }
    });
  }

  /**
   * Mapea un curso del backend a la tarjeta del dashboard
   */
  private mapCursoToCard(curso: CursoProgreso): Course {
    // Colores basados en el tipo de curso
    const colors = ['#FFD966', '#6B8AFF', '#FFB4B4', '#77d27a', '#FF9D76', '#B4A7FF'];
    const emojis = ['📚', '🎨', '💼', '🚀', '💡', '🎯'];
    
    const colorIndex = curso.id_curso % colors.length;
    
    // Extraer tags del título o descripción
    let tags: string[] = [];
    if (curso.es_premium) {
      tags.push('Premium');
    }
    tags.push(`${curso.total_modulos} módulos`);
    
    // Formatear fecha de inicio
    const fechaInscripcion = new Date(curso.fecha_inscripcion);
    const startDate = fechaInscripcion.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short' 
    });

    return {
      id: curso.id_curso,
      title: curso.titulo,
      description: curso.descripcion,
      image: emojis[colorIndex],
      imageColor: colors[colorIndex],
      tags: tags,
      rating: 4.5, // Por ahora fijo, luego puede venir del backend
      startDate: startDate,
      porcentaje: curso.porcentaje,
      clases_completadas: curso.clases_completadas,
      total_clases: curso.total_clases,
      es_premium: curso.es_premium
    };
  }

  /**
   * Convierte una fecha UTC string a Date tratándola como hora local
   * Las fechas en la BD vienen con 'Z' pero ya están en hora local de El Salvador
   */
  private parseAsLocalDate(dateString: string): Date {
    // Remover la 'Z' y cualquier indicador de zona horaria
    const cleanDateString = dateString.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
    
    // Parsear como fecha local
    const date = new Date(cleanDateString);
    
    return date;
  }

  /**
   * Establece el mes actual
   */
  private setCurrentMonth() {
    const now = new Date();
    const monthName = now.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    this.currentMonth.set(monthName.charAt(0).toUpperCase() + monthName.slice(1));
  }
  // Schedule computed desde los eventos
  schedule = computed(() => {
    const eventos = this.eventosCalendario();
    return eventos.slice(0, 5).map(evento => {
      const fecha = this.parseAsLocalDate(evento.fecha_inicio);
      const fechaFin = this.parseAsLocalDate(evento.fecha_fin);
      return {
        id: evento.id,
        date: fecha.getDate().toString().padStart(2, '0'),
        title: evento.titulo,
        time: `${fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} - ${fechaFin.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`,
        progress: `${evento.inscritos} / ${evento.capacidad_maxima} inscritos`
      };
    });
  });

  /**
   * Genera los días del calendario dinámicamente
   */
  private generateCalendarDays() {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Resetear horas para comparación limpia
    
    const currentDay = today.getDay(); // 0 = Domingo, 1 = Lunes, etc.
    const daysOfWeek = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    
    // Calcular el lunes de esta semana
    const monday = new Date(today);
    const diff = today.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    
    const days: CalendarDay[] = [];
    
    // Generar 7 días desde el lunes
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      date.setHours(0, 0, 0, 0);
      
      days.push({
        day: daysOfWeek[i],
        date: date.getDate(),
        fullDate: date,
        active: date.getTime() === today.getTime(),
        hasEvent: false,
        eventos: []
      });
    }
    
    console.log('📅 Días generados del calendario:', days.map(d => ({
      dia: d.day,
      fecha: d.fullDate.toLocaleDateString('es-ES'),
      active: d.active
    })));
    
    this.calendarDaysSignal.set(days);
  }

  // Computed para eventos en vivo
  liveEvents = computed(() => this.eventosReales());

  setFilter(filter: string) {
    this.activeFilter.set(filter);
  }

  viewCourse(courseId: number) {
    this.router.navigate(['/course', courseId]);
  }

  joinLiveEvent(eventId: number) {
    this.router.navigate(['/live-class', eventId]);
  }

  scrollLiveEvents(direction: 'prev' | 'next') {
    const container = this.liveCarouselRef?.nativeElement;
    if (!container) return;

    // Scroll de una tarjeta a la vez (320px + 16px gap = 336px)
    const scrollAmount = 340;
    
    if (direction === 'next') {
      container.scrollLeft += scrollAmount;
    } else {
      container.scrollLeft -= scrollAmount;
    }

    setTimeout(() => this.updateCarouselControls(), 350);
  }

  onCarouselScroll() {
    this.updateCarouselControls();
  }

  private updateCarouselControls() {
    const container = this.liveCarouselRef?.nativeElement;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScroll = scrollWidth - clientWidth;

    // Solo mostrar controles si hay contenido para scroll
    if (maxScroll <= 10) {
      this.canScrollPrev.set(false);
      this.canScrollNext.set(false);
      return;
    }

    this.canScrollPrev.set(scrollLeft > 15);
    this.canScrollNext.set(scrollLeft < maxScroll - 15);
  }

  /**
   * Carga eventos en vivo del backend
   */
  loadEventosEnVivo() {
    this.eventosService.getEventosDisponibles().subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          // Mapear eventos del backend al formato del componente
          const eventos: LiveEvent[] = response.data.map((evento: EventoEnVivo) => {
            // Construir URL de la imagen desde Google Cloud Storage
            const imagenPortada = evento.imagen_portada 
              ? `https://storage.googleapis.com/traidinglab/eventos/portadas/${evento.imagen_portada}`
              : 'https://storage.googleapis.com/traidinglab/eventos/portadas/e823bbf3-5eaf-492e-8613-fb6574a81c33.jpeg'; // Imagen por defecto
            
            return {
              id: evento.id,
              title: evento.titulo,
              instructor: evento.instructor,
              participants: evento.inscritos,
              duration: this.eventosService.getDuracionEvento(evento),
              category: evento.estado === 'EN_VIVO' ? 'EN VIVO AHORA' : 'Próximamente',
              thumbnail: imagenPortada,
              color: evento.color || '#3788d8'
            };
          });
          
          this.eventosReales.set(eventos);
          console.log('📺 Eventos cargados:', eventos.length);
          console.log('🖼️ Imágenes:', eventos.map(e => ({ titulo: e.title, imagen: e.thumbnail })));
          setTimeout(() => this.updateCarouselControls(), 120);
        } else {
          this.eventosReales.set([]);
          setTimeout(() => this.updateCarouselControls(), 120);
        }
      },
      error: (error) => {
        console.error('Error al cargar eventos:', error);
        setTimeout(() => this.updateCarouselControls(), 120);
      }
    });
  }

  /**
   * Carga todos los eventos para el calendario
   */
  loadEventosCalendario() {
    console.log('🔄 Iniciando carga de eventos del calendario...');
    console.log('🌎 Zona horaria del navegador:', Intl.DateTimeFormat().resolvedOptions().timeZone);
    
    // Intentar primero con mis-eventos, si falla usar disponibles
    this.eventosService.getMisEventos().subscribe({
      next: (response) => {
        console.log('📡 Respuesta de /eventos/mis-eventos:', response);
        
        if (response.ok && response.data && response.data.length > 0) {
          console.log('📅 Eventos recibidos de mis-eventos:', response.data);
          this.eventosCalendario.set(response.data);
          this.mapEventosToCalendar(response.data);
          console.log('✅ Eventos del calendario cargados:', response.data.length);
        } else {
          console.log('⚠️ No hay eventos en mis-eventos, intentando con disponibles...');
          // Si no hay eventos en mis-eventos, intentar con disponibles
          this.loadEventosDisponibles();
        }
      },
      error: (error) => {
        console.error('❌ Error en mis-eventos, intentando con disponibles...', error);
        this.loadEventosDisponibles();
      }
    });
  }

  /**
   * Carga eventos disponibles (fallback)
   */
  private loadEventosDisponibles() {
    this.eventosService.getEventosDisponibles().subscribe({
      next: (response) => {
        console.log('📡 Respuesta de /eventos/disponibles:', response);
        
        if (response.ok && response.data) {
          console.log('📅 Eventos recibidos de disponibles:', response.data);
          this.eventosCalendario.set(response.data);
          this.mapEventosToCalendar(response.data);
          console.log('✅ Eventos disponibles cargados:', response.data.length);
        } else {
          console.warn('⚠️ No hay eventos disponibles:', response);
        }
      },
      error: (error) => {
        console.error('❌ Error al cargar eventos disponibles:', error);
      }
    });
  }

  /**
   * Mapea los eventos a los días del calendario
   */
  private mapEventosToCalendar(eventos: EventoEnVivo[]) {
    const days = this.calendarDaysSignal();
    
    console.log('🔍 Mapeando eventos al calendario...');
    console.log('📅 Días en el calendario:', days.map(d => ({
      dia: d.day,
      fecha: d.fullDate.toLocaleDateString('es-SV'),
      year: d.fullDate.getFullYear(),
      month: d.fullDate.getMonth(),
      date: d.fullDate.getDate()
    })));
    
    console.log('📺 Eventos a mapear:', eventos.map(e => {
      const fecha = this.parseAsLocalDate(e.fecha_inicio);
      return {
        titulo: e.titulo,
        fechaOriginal: e.fecha_inicio,
        fechaLocal: fecha.toLocaleDateString('es-SV'),
        year: fecha.getFullYear(),
        month: fecha.getMonth(),
        date: fecha.getDate()
      };
    }));
    
    const updatedDays = days.map(day => {
      // Obtener año, mes y día del calendario
      const dayYear = day.fullDate.getFullYear();
      const dayMonth = day.fullDate.getMonth();
      const dayDate = day.fullDate.getDate();
      
      // Filtrar eventos que ocurren en este día
      const eventosDelDia = eventos.filter(evento => {
        // Parsear la fecha como hora local (las fechas en BD ya están en hora local)
        const fechaEvento = this.parseAsLocalDate(evento.fecha_inicio);
        
        // Comparar año, mes y día (en hora local del navegador)
        const match = (
          fechaEvento.getFullYear() === dayYear &&
          fechaEvento.getMonth() === dayMonth &&
          fechaEvento.getDate() === dayDate
        );
        
        if (match) {
          console.log(`✅ MATCH! Evento "${evento.titulo}"`);
          console.log(`   Evento: ${fechaEvento.getDate()}/${fechaEvento.getMonth() + 1}/${fechaEvento.getFullYear()}`);
          console.log(`   Día: ${dayDate}/${dayMonth + 1}/${dayYear}`);
        } else {
          console.log(`❌ NO MATCH: Evento "${evento.titulo}"`);
          console.log(`   Evento: ${fechaEvento.getDate()}/${fechaEvento.getMonth() + 1}/${fechaEvento.getFullYear()}`);
          console.log(`   Día: ${dayDate}/${dayMonth + 1}/${dayYear}`);
        }
        
        return match;
      });
      
      return {
        ...day,
        hasEvent: eventosDelDia.length > 0,
        eventos: eventosDelDia
      };
    });
    
    console.log('✨ Días actualizados con eventos:', updatedDays.filter(d => d.hasEvent).map(d => ({
      dia: d.day,
      fecha: d.fullDate.toLocaleDateString('es-SV'),
      eventos: d.eventos?.length,
      titulos: d.eventos?.map(e => e.titulo)
    })));
    
    this.calendarDaysSignal.set(updatedDays);
  }

  /**
   * Navega al primer evento del día seleccionado
   */
  onCalendarDayClick(day: CalendarDay) {
    if (day.hasEvent && day.eventos && day.eventos.length > 0) {
      // Navegar al primer evento del día
      const primerEvento = day.eventos[0];
      this.router.navigate(['/live-class', primerEvento.id]);
    }
  }
}

